import { useTranslation } from 'react-i18next';
import { Music } from 'lucide-react';
import { useMediaPlayer } from '@/presentation/hook/useMediaPlayer';
import { MediaPlayerControls } from '@/presentation/component/course/MediaPlayerControls';
import { MediaErrorNotice } from '@/presentation/component/course/MediaErrorNotice';

interface AudioPlayerProps {
  audioUrl: string;
  title: string;
  initialResumePercent?: number;
  onProgress?: (progress: number) => void;
  onComplete?: () => void;
}

export const AudioPlayer = ({
  audioUrl,
  title,
  initialResumePercent,
  onProgress,
  onComplete,
}: AudioPlayerProps) => {
  const { t } = useTranslation();
  const player = useMediaPlayer<HTMLAudioElement>({
    src: audioUrl,
    initialResumePercent,
    onProgress,
    onComplete,
  });

  if (player.hasError) {
    return <MediaErrorNotice />;
  }

  return (
    <div
      className="rounded-lg border border-[var(--color-line)] bg-[var(--color-surface-2)] p-6 text-[var(--color-ink)]"
      data-testid="audio-player"
    >
      <audio ref={player.mediaRef} src={audioUrl} data-testid="audio-element" />

      <div className="mb-6 flex flex-col items-center gap-3 text-center sm:flex-row sm:text-left">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-[var(--color-accent-soft)]">
          <Music className="h-10 w-10 text-[var(--color-accent-ink)]" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-lg font-semibold">{title}</p>
          <p className="text-sm text-[var(--color-ink-faint)]">{t('coursePlayer.audioLesson')}</p>
        </div>
      </div>

      <MediaPlayerControls player={player} />
    </div>
  );
};
